import { createFileRoute, notFound } from "@tanstack/react-router";
import { EarningAppShell, buildEarningAppLd } from "@/components/earning-apps/EarningAppShell";
import { findEarningApp } from "@/lib/earning-apps/apps";
import { getCustomEarningApp } from "@/lib/earning-apps/admin.functions";
import { SITE_URL } from "@/lib/seo";
import { getSiteContext } from "@/lib/site-context.functions";
import { videoFromOverride } from "@/lib/site-context";
import { earningAppVideoIntro, earningAppVideoKey, MAX_EARNING_APP_VIDEOS, parseEarningAppYouTubeId } from "@/lib/earning-apps/youtube";
import { buildVideoObjectLd } from "@/lib/video-embeds";

export const Route = createFileRoute("/earning-apps-review/$slug")({
  loader: async ({ params }) => {
    const [app, context] = await Promise.all([
      Promise.resolve(findEarningApp(params.slug)).then(async (staticApp) => staticApp ?? (await getCustomEarningApp({ data: { slug: params.slug } })).app),
      getSiteContext(),
    ]);
    if (!app) throw notFound();
    const videos = Array.from({ length: MAX_EARNING_APP_VIDEOS }, (_, i) => {
      const row = context.videos[earningAppVideoKey(params.slug, i + 1)];
      const video = row && parseEarningAppYouTubeId(row.video_id) ? videoFromOverride(row) : undefined;
      return video ? { ...video, slot: i + 1, intro: earningAppVideoIntro(row?.description, app.name) } : undefined;
    }).filter((video) => video !== undefined);
    return { app, videos };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return { meta: [{ title: "App not found" }, { name: "robots", content: "noindex" }] };
    const { app } = loaderData;
    const title = `${app.name} Real Or Fake? Honest Review | DrHint`;
    const description = `Is ${app.name} legit? First-hand ${app.category.toLowerCase()} review — payout methods, min withdrawal, real signals and red flags. DrHint verdict: ${app.verdict}.`;
    const url = `${SITE_URL}/earning-apps-review/${app.slug}`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [...buildEarningAppLd(app), ...loaderData.videos.map((video) => buildVideoObjectLd({ video, pageUrl: url }))],
    };
  },
  component: EarningAppPage,
  notFoundComponent: NotFoundBox,
  errorComponent: ErrorBox,
});

function EarningAppPage() {
  const { app, videos } = Route.useLoaderData();
  return <EarningAppShell app={app} videos={videos} />;
}

function NotFoundBox() {
  return (
    <div className="mx-auto max-w-3xl px-4 py-24 text-center">
      <h1 className="text-2xl font-bold">App not found in the DrHint review directory</h1>
      <p className="mt-3 text-muted-foreground">Browse the full list to find a similar earning app.</p>
    </div>
  );
}

function ErrorBox({ reset }: { reset: () => void }) {
  return (
    <div className="mx-auto max-w-3xl px-4 py-24 text-center">
      <h1 className="text-2xl font-bold">Something went wrong loading this review</h1>
      <button onClick={() => reset()} className="mt-4 rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold">Retry</button>
    </div>
  );
}